import { orderDisplayNumber } from '../utils/orderDisplay';

/**
 * Сервис уведомлений для баристы:
 * - Системные уведомления браузера (видны при свёрнутой вкладке)
 * - Звук при новом заказе и «пора готовить» — из файлов в public/sounds/
 *
 * Файлы: public/sounds/new-order.mp3 и public/sounds/time.mp3 (порог «5 мин до выдачи»).
 * При других именах поменяйте константы SOUND_* ниже. Форматы: mp3, ogg, wav.
 * Для громкого сигнала в зале возьмите короткий звук с высокой громкостью в самом файле
 * (нормализация/лимитер в аудиоредакторе) — в браузере максимум это volume = 1 и громкость ПК/колонок.
 */

const NOTIFICATION_TAG = 'RCoffee-new-order';

const base = process.env.PUBLIC_URL || '';
const SOUND_NEW_ORDER = `${base}/sounds/new-order.mp3`;
/** Звук «за 5 мин до выдачи» — имя файла в public/sounds/ */
const SOUND_TIME_TO_PREPARE = `${base}/sounds/time.mp3`;

/** @type {Map<string, HTMLAudioElement>} */
const audioPreloadCache = new Map();

/** Разблокировка после жеста пользователя (без этого браузер часто блокирует play() при SSE). */
let mediaPlaybackUnlocked = false;

/** Общий контекст для запасного синтетического звука (создаётся в unlock). */
let sharedAudioContext = null;

function getPreloadedAudio(url) {
  let el = audioPreloadCache.get(url);
  if (!el) {
    el = new Audio(url);
    el.preload = 'auto';
    audioPreloadCache.set(url, el);
  }
  return el;
}

/**
 * Вызвать из обработчика жеста (клик «Войти», первый тап на доске).
 * Без этого звук по событию с сервера часто не слышен (NotAllowedError).
 */
export async function unlockNotificationAudio() {
  if (mediaPlaybackUnlocked) {
    return;
  }
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC && !sharedAudioContext) {
      sharedAudioContext = new AC();
    }
    if (sharedAudioContext?.state === 'suspended') {
      await sharedAudioContext.resume();
    }
  } catch (e) {
    console.warn('[Notifications] AudioContext unlock:', e);
  }

  const touchAudio = async (url) => {
    try {
      const audio = getPreloadedAudio(url);
      const prevVolume = audio.volume;
      audio.volume = 0.01;
      audio.currentTime = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audio.volume = prevVolume;
    } catch (e) {
      console.warn('[Notifications] Разблокировка HTMLAudio:', url, e);
    }
  };

  await touchAudio(SOUND_NEW_ORDER);
  await touchAudio(SOUND_TIME_TO_PREPARE);

  mediaPlaybackUnlocked = true;
}

/** Двойной бип, если нет файла или play() запрещён */
function playSyntheticNewOrderSound() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
      return;
    }
    const ctx =
      sharedAudioContext && sharedAudioContext.state !== 'closed'
        ? sharedAudioContext
        : new AudioContext();
    const playBeep = (startTime, frequency = 880) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gainNode.gain.setValueAtTime(0.35, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.18);
      oscillator.start(startTime);
      oscillator.stop(startTime + 0.18);
    };
    const run = () => {
      playBeep(0);
      playBeep(0.28);
    };
    if (ctx.state === 'suspended') {
      ctx.resume().then(run).catch(() => {});
    } else {
      run();
    }
  } catch (e) {
    console.warn('[Notifications] Синтетический звук недоступен:', e);
  }
}

function playSyntheticTimeToPrepareSound() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
      return;
    }
    const ctx =
      sharedAudioContext && sharedAudioContext.state !== 'closed'
        ? sharedAudioContext
        : new AudioContext();
    const playBeep = (startTime, frequency, duration = 0.22) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gainNode.gain.setValueAtTime(0.4, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    const run = () => {
      playBeep(0, 880);
      playBeep(0.38, 1100);
      playBeep(0.76, 1320);
    };
    if (ctx.state === 'suspended') {
      ctx.resume().then(run).catch(() => {});
    } else {
      run();
    }
  } catch (e) {
    console.warn('[Notifications] Синтетический звук (пора готовить):', e);
  }
}

/**
 * @param {string} url
 * @param {number} [volume] 0..1 (в коде обычно 1 — максимум в браузере)
 * @param {() => void} [onFallback]
 */
function playAudioFile(url, volume = 1, onFallback) {
  try {
    const audio = getPreloadedAudio(url);
    if (audio.error) {
      console.warn('[Notifications] Файл звука недоступен (проверьте public/sounds):', url, audio.error);
      onFallback?.();
      return;
    }
    audio.volume = Math.min(1, Math.max(0, volume));
    audio.currentTime = 0;
    const promise = audio.play();
    if (promise !== undefined) {
      promise.catch((err) => {
        console.warn('[Notifications] play() отклонён, запасной сигнал:', url, err);
        onFallback?.();
      });
    }
  } catch (e) {
    console.warn('[Notifications] Ошибка Audio:', e);
    onFallback?.();
  }
}

/** Отдельный экземпляр Audio на каждый вызов — надёжнее для отложенного сигнала (таймер), чем один закэшированный элемент */
function playAudioFileOneShot(url, volume = 1, onFallback) {
  try {
    const audio = new Audio(url);
    audio.volume = Math.min(1, Math.max(0, volume));
    const promise = audio.play();
    if (promise !== undefined) {
      promise.catch((err) => {
        console.warn('[Notifications] one-shot play() отклонён:', url, err);
        onFallback?.();
      });
    }
  } catch (e) {
    console.warn('[Notifications] one-shot Audio:', e);
    onFallback?.();
  }
}

/**
 * Запрос разрешения на уведомления (вызывать после входа баристы).
 * @returns {Promise<'granted'|'denied'|'default'>}
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.warn('[Notifications] Browser does not support Notification API');
    return 'denied';
  }
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  if (Notification.permission === 'denied') {
    return 'denied';
  }
  try {
    const permission = await Notification.requestPermission();
    console.log('[Notifications] Permission:', permission);
    return permission;
  } catch (e) {
    console.error('[Notifications] Request permission error:', e);
    return 'denied';
  }
}

/**
 * Показать системное уведомление о новом заказе (работает при свёрнутой вкладке).
 * @param {{ id: number, items?: Array<{ item_name?: string }> }} order
 */
export function showNewOrderNotification(order) {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }
  const title = `Новый заказ #${orderDisplayNumber(order)}`;
  const itemNames = (order.items || [])
    .slice(0, 3)
    .map((item) => item.item_name || 'Позиция')
    .join(', ');
  const body = itemNames ? `Состав: ${itemNames}${(order.items?.length || 0) > 3 ? '...' : ''}` : 'Откройте вкладку для просмотра';

  try {
    const notification = new Notification(title, {
      body,
      tag: NOTIFICATION_TAG,
      requireInteraction: true,
      icon: '/favicon.ico',
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (e) {
    console.error('[Notifications] Show notification error:', e);
  }
}

/**
 * Звук о новом заказе — файл public/sounds/new-order.mp3 (или замените расширение в SOUND_NEW_ORDER).
 */
export function playNewOrderSound() {
  playAudioFile(SOUND_NEW_ORDER, 1, playSyntheticNewOrderSound);
}

const NOTIFICATION_TAG_PREPARE = 'RCoffee-time-to-prepare';

/**
 * Звук «пора готовить» — файл public/sounds/time.mp3
 */
export function playTimeToPrepareSound() {
  playAudioFileOneShot(SOUND_TIME_TO_PREPARE, 1, playSyntheticTimeToPrepareSound);
}

/**
 * Ранняя подгрузка звуков (после первого жеста пользователя можно вызвать из приложения).
 * Уменьшает задержку перед первым сигналом о заказе.
 */
export function preloadNotificationSounds() {
  getPreloadedAudio(SOUND_NEW_ORDER);
  getPreloadedAudio(SOUND_TIME_TO_PREPARE);
}

/**
 * Показать системное уведомление «пора готовить заказ».
 * @param {{ id: number, estimated_ready_time?: string }} order
 */
export function showTimeToPrepareNotification(order) {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }
  const title = `Пора готовить заказ #${orderDisplayNumber(order)}`;
  const body = 'До времени готовности осталось 5 минут';

  try {
    const notification = new Notification(title, {
      body,
      tag: NOTIFICATION_TAG_PREPARE,
      requireInteraction: true,
      icon: '/favicon.ico',
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (e) {
    console.error('[Notifications] Show time-to-prepare notification error:', e);
  }
}
